import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Typography } from "@mui/material";
import { ImageWithLockedAspectRatio } from "shared-ui/Image/ImageWithLockedAspectRatio";
import { Loading } from "shared-ui/global/Loading";
import { useState } from "react";
import CropperModal from "../../universal/Image/CropModal";
import ImageFileDragAndDrop from "../../universal/Image/ImageFileDragAndDrop";
import { useUploadPhoto } from "../../universal/Image/uploadPhoto";
import { MenuItemChangeImageAction } from "../reducer";

const REDESIGN_IMAGE_HEIGHT = 240;

interface RedesignImageProps {
  imageUrl: string | null;
  onChangeImage: (args: MenuItemChangeImageAction["payload"]) => void;
}

/** Figma Image card — Kitchen menu item redesign only. Legacy uses `Image.tsx`. */
export const RedesignImage = ({ imageUrl, onChangeImage }: RedesignImageProps) => {
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);

  const [uploadPhotos, imagesUploading] = useUploadPhoto({
    onComplete: (nextImageUrl: string): void => {
      if (nextImageUrl) {
        onChangeImage({ imageUrl: nextImageUrl });
      }
    },
  });

  const handleCropComplete = async (croppedImage: string) => {
    uploadPhotos(croppedImage);
  };

  if (imagesUploading) {
    return <Loading />;
  }

  if (imageUrl) {
    return (
      <Box sx={{ position: "relative", height: REDESIGN_IMAGE_HEIGHT }}>
        <Box sx={{ height: "100%" }}>
          <ImageWithLockedAspectRatio
            imageUrl={imageUrl}
            borderRadius={"1"}
            sx={{
              height: REDESIGN_IMAGE_HEIGHT,
              width: "100%",
              paddingTop: 0,
            }}
          />
        </Box>
        <Box sx={{ position: "absolute", top: 0.5, left: 1.5 }}>
          <IconButton
            sx={{
              color: "white",
              backgroundColor: "black",
              opacity: 0.6,
              "&:hover": {
                color: "white",
                backgroundColor: "black",
              },
            }}
            onClick={() => onChangeImage({ imageUrl: null })}
            size="small"
            aria-label="Delete"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: REDESIGN_IMAGE_HEIGHT }}>
        <ImageFileDragAndDrop
          onUpload={setImageToCrop}
          sx={{
            bgcolor: "greyscale.50",
            borderColor: "greyscale.200",
            borderRadius: "6px",
          }}
        >
          <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
            Click to upload or drop an image
          </Typography>
        </ImageFileDragAndDrop>
      </Box>
      {imageToCrop && (
        <CropperModal
          open={!!imageToCrop}
          onClose={() => setImageToCrop(null)}
          imageUrl={imageToCrop}
          onSave={handleCropComplete}
        />
      )}
    </>
  );
};
